'use client';

import type { Filter, SRPFilterAction, SRPState } from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import { EncodeFiltersToSearchParams } from '@dealerportal/utilities';

function buildSRPUrl(filters: Filter[], sort?: string): string {
  const clientQuery = new URL(
    window.location.pathname,
    'https://any-base-path-will-do-here.com'
  );
  const newSearchParams = EncodeFiltersToSearchParams(
    clientQuery.searchParams,
    filters
  );
  if (sort) {
    newSearchParams.set('sort', sort);
  }
  let search = `?${newSearchParams.toString()}`;
  if (search.length === 1) search = '';
  return `/p${search}`;
}

export function ApplyFilterAction(
  state: SRPState,
  action: SRPFilterAction & { filter: string }
): SRPState {
  const newFilters: Filter[] = JSON.parse(
    JSON.stringify(state.filters)
  ) as Filter[];
  const filter = newFilters.find(({ name }) => name === action.filter);
  if (filter == null) return state;
  if (filter.enabledValues == null) filter.enabledValues = [];
  switch (action.type) {
    case 'SET_ALL_TO': {
      filter.enabledValues = [action.value];
      break;
    }
    case 'UPDATE': {
      filter.enabledValues.push(action.value);
      break;
    }
    case 'REMOVE': {
      const indexOf = filter.enabledValues.findIndex(
        (value) =>
          value === action.value ||
          `${value}`.toLowerCase() === `${action.value}`.toLowerCase()
      );
      if (indexOf === -1) {
        logger.warn(
          `Couldn't find filter (${action.filter}: ${action.value}) to remove`
        );
      }
      filter.enabledValues = [
        ...filter.enabledValues.slice(0, indexOf),
        ...filter.enabledValues.slice(indexOf + 1, filter.enabledValues.length),
      ];
      break;
    }
  }
  // Deduplicate enabled values
  filter.enabledValues = Array.from(new Set(filter.enabledValues));
  const ret = { ...state, filters: newFilters, needsHydration: true };
  return ret;
}

export function HydrateFilters(
  currentFilters: Filter[],
  filtersFromServer: Filter[]
): Filter[] {
  const newFilters = [...currentFilters];
  for (const filter of newFilters) {
    for (const filterValue in filter.potentialValues) {
      filter.potentialValues[filterValue] =
        filtersFromServer?.find((f) => f.name === filter.name)
          ?.potentialValues?.[filterValue] ?? 0;
    }
  }
  return newFilters;
}

export const SRPReducerFactory = (
  push: (url: string) => void
): ((state: SRPState, action: SRPFilterAction) => SRPState) => {
  return (state: SRPState, action: SRPFilterAction): SRPState => {
    let doNavigation = false;
    switch (action?.type) {
      case 'RESET':
        state.filters = action.filters;
        state.loading = false;
        break;
      case 'CLEAR_ALL':
        for (const filter of state.filters) {
          filter.enabledValues = [];
        }
        doNavigation = true;
        break;
      case 'APPLY':
        doNavigation = true;
        break;
      case 'HYDRATE_FILTERS':
        state.filters = HydrateFilters(state.filters, action.hydratedFilters);
        state.needsHydration = false;
        break;
      case 'HYDRATE_FILTERS_FAIL':
        state.needsHydration = false;
        break;
      case 'UPDATE_SORT':
        state.sort = action.value;
        doNavigation = true;
        break;
      default:
        state = ApplyFilterAction(state, action);
        break;
    }
    if (doNavigation) push(buildSRPUrl(state.filters, state.sort));
    return { ...state };
  };
};
